import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Play, Square, TrendingUp, TrendingDown, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { API } from '../config';

// Mismo ícono humanoide-IA que ya usa el sidebar para "IA Atenea" -- evita
// cualquier representación tipo muñeco/caricatura de una mujer.
const AteneaIcon = ({ size = 22, color = '#00D4FF' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3C9 3 7 5.5 7 8c0 2 1 3.5 2.5 4.5V14h5v-1.5C16 11.5 17 10 17 8c0-2.5-2-5-5-5z" />
    <path d="M9.5 14v1.5h5V14" />
    <path d="M7 15.5C5 16 4 17 4 18.5h16c0-1.5-1-2.5-3-3" />
    <circle cx="10" cy="8" r="0.8" fill={color} stroke="none" />
    <circle cx="14" cy="8" r="0.8" fill={color} stroke="none" />
    <circle cx="12" cy="5.5" r="0.6" fill={color} stroke="none" />
    <path d="M7 7.5 L5.5 6" strokeWidth="1" opacity="0.6" />
    <path d="M17 7.5 L18.5 6" strokeWidth="1" opacity="0.6" />
    <path d="M7 9 L5.5 9.5" strokeWidth="1" opacity="0.6" />
    <path d="M17 9 L18.5 9.5" strokeWidth="1" opacity="0.6" />
  </svg>
);

// Verde y azul claro mezclados (los mismos que ya usa el Scanner) -- rojo
// queda reservado solo para alertas/pérdidas, no para uso decorativo.
const ScoreBadge = ({ score }) => {
  const color = score >= 90 ? '#FFD700' : score >= 70 ? '#00FF88' : score >= 50 ? '#00D4FF' : '#52525B';
  const bg = score >= 90 ? 'rgba(255,215,0,0.1)' : score >= 70 ? 'rgba(0,255,136,0.1)' : score >= 50 ? 'rgba(0,212,255,0.1)' : 'rgba(82,82,91,0.15)';
  return (
    <div style={{ background: bg, border: `1px solid ${color}30`, color }}
      className="flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold min-w-[40px]">
      {score}
    </div>
  );
};

// Pedido explícito: siempre 8 decimales (0.00000000), igual que el resto
// de los montos de la plataforma -- antes se redondeaba a 2 decimales para
// montos "grandes", inconsistente con el resto.
const formatPnl = (v) => {
  const n = Number(v) || 0;
  return n.toFixed(8);
};

const AteneaTrader = () => {
  const [loading, setLoading] = useState(true);
  const [exchanges, setExchanges] = useState([]);
  const [instances, setInstances] = useState([]);
  const [histories, setHistories] = useState({});
  const [form, setForm] = useState({
    exchange_id: '', market_type: 'spot', leverage: 1,
    usd_por_trade: '50', budget_usd: '500',
    stop_loss_pct: '', take_profit_pct: '',
  });
  const pollRef = useRef(null);
  // Atenea se puede reconfigurar mientras esta PARADA. Para eso el formulario
  // tiene que arrancar con lo que ella tiene puesto hoy, no con los valores
  // por defecto. Se rellena UNA sola vez por bot: load() se repite cada 20 s
  // y si rellenara siempre, te pisaria lo que estas escribiendo.
  const seedRef = useRef(null);
  const [guardando, setGuardando] = useState(false);

  const load = async () => {
    try {
      const [exRes, botsRes] = await Promise.all([
        axios.get(`${API}/exchanges/keys`, { withCredentials: true }),
        axios.get(`${API}/bots`, { withCredentials: true }),
      ]);
      setExchanges(exRes.data.keys || []);
      const propias = (botsRes.data.bots || []).filter(b => b.bot_template_id === 'atenea_trader');
      setInstances(propias);
      const _at = propias[0];
      if (_at && seedRef.current !== _at.bot_id) {
        seedRef.current = _at.bot_id;
        const _sp = _at.strategy_params || {};
        setForm(f => ({
          ...f,
          exchange_id: _at.exchange_id || f.exchange_id,
          market_type: _at.market_type || f.market_type,
          leverage: _at.leverage ?? f.leverage,
          usd_por_trade: _sp.usd_por_trade ?? f.usd_por_trade,
          budget_usd: _at.budget_usd ?? f.budget_usd,
          stop_loss_pct: _sp.stop_loss_pct ?? '',
          take_profit_pct: _sp.take_profit_pct ?? '',
        }));
      }
      setForm(f => ({ ...f, exchange_id: f.exchange_id || (exRes.data.keys || [])[0]?.exchange_id || '' }));
      propias.forEach(async (b) => {
        try {
          const r = await axios.get(`${API}/aegis/trades`, {
            params: { bot_id: b.bot_id, limit: 5000 }, withCredentials: true });
          setHistories(prev => ({ ...prev, [b.bot_id]: r.data.trades || [] }));
        } catch { /* silencioso -- no bloquea el dashboard */ }
      });
    } catch {
      toast.error('Error al cargar Atenea');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 20000);
    return () => clearInterval(pollRef.current);
  }, []); // eslint-disable-line

  const activar = async (e) => {
    e.preventDefault();
    if (!form.exchange_id) { toast.error('Elegí un exchange'); return; }
    // Encontrado en producción: $50 por operación sobre $100 de presupuesto
    // (la mitad del total en un solo trade) hace que cualquier pérdida
    // chica se vea como un drawdown enorme y dispare el freno de emergencia
    // de la cuenta casi de inmediato. Tope: como mucho 25% del presupuesto
    // por operación -- deja margen para varias operaciones seguidas sin
    // que una sola pueda mover el drawdown de forma exagerada.
    const _usdOp = parseFloat(form.usd_por_trade) || 0;
    const _presupuesto = parseFloat(form.budget_usd) || 0;
    if (_presupuesto > 0 && _usdOp > _presupuesto * 0.25) {
      toast.error(`El monto por operación ($${_usdOp}) es muy grande frente al presupuesto ($${_presupuesto}) -- máximo 25% (${(_presupuesto * 0.25).toFixed(0)}). Una sola pérdida chica se vería como un drawdown enorme.`);
      return;
    }
    try {
      const r0 = await axios.post(`${API}/bots`, {
        name: 'Atenea',
        bot_template_id: 'atenea_trader',
        exchange_id: form.exchange_id,
        symbol: 'BTC/USDT',
        market_type: form.market_type,
        leverage: parseInt(form.leverage) || 1,
        trade_amount: 0.001,
        budget_usd: parseFloat(form.budget_usd) || null,
        strategy_params: {
          timeframe: '1h',
          monitor_interval_min: 1,
          usd_por_trade: parseFloat(form.usd_por_trade) || 50,
          ...(form.stop_loss_pct !== '' ? { stop_loss_pct: parseFloat(form.stop_loss_pct) } : {}),
          ...(form.take_profit_pct !== '' ? { take_profit_pct: parseFloat(form.take_profit_pct) } : {}),
        },
      }, { withCredentials: true });
      const nuevoId = r0?.data?.bot_id;
      // Crear el bot lo deja en "stopped" (igual que cualquier otro bot de
      // la plataforma) -- lo arrancamos enseguida para que no haga falta
      // un segundo click, y el usuario vea el escáner activo de inmediato.
      if (nuevoId) {
        try { await axios.post(`${API}/bots/${nuevoId}/start`, {}, { withCredentials: true }); } catch { /* se puede iniciar a mano si falla */ }
      }
      toast.success('Atenea activada');
      load();
      // Sondeo rápido los primeros segundos -- el barrido de 40 criptos
      // tarda unos segundos reales; sin esto el usuario espera hasta 20s
      // (el intervalo normal) para ver el escáner poblado por primera vez.
      let intentos = 0;
      const rapido = setInterval(() => {
        intentos += 1;
        load();
        if (intentos >= 8) clearInterval(rapido);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar Atenea');
    }
  };

  const start = async (id) => { try { await axios.post(`${API}/bots/${id}/start`, {}, { withCredentials: true }); toast.success('Iniciada'); load(); } catch { toast.error('Error al iniciar'); } };
  const stop = async (id) => { try { await axios.post(`${API}/bots/${id}/stop`, {}, { withCredentials: true }); toast.success('Detenida'); load(); } catch { toast.error('Error al detener'); } };
  const eliminar = async (id) => {
    if (!window.confirm('¿Desactivar esta instancia de Atenea?')) return;
    try { await axios.delete(`${API}/bots/${id}`, { withCredentials: true }); toast.success('Desactivada'); load(); } catch { toast.error('Error al desactivar'); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div>
        </div>
      </Layout>
    );
  }

  const instance = instances[0] || null;
  // Mientras Atenea esta PARADA se le pueden cambiar los datos (pedido
  // explicito). Corriendo, no: cambiarle el exchange o el monto en caliente
  // le moveria el piso a una decision ya tomada.
  const editable = !instance || instance.status !== 'running';

  const guardarCambios = async () => {
    if (!instance) return;
    // Mismo tope que ya valida la creacion: como mucho 25% del presupuesto
    // por operacion.
    const _usdOp = parseFloat(form.usd_por_trade) || 0;
    const _presupuesto = parseFloat(form.budget_usd) || 0;
    if (_presupuesto > 0 && _usdOp > _presupuesto * 0.25) {
      toast.error(`El monto por operacion ($${_usdOp}) es muy grande frente al presupuesto ($${_presupuesto}) -- maximo 25% (${(_presupuesto * 0.25).toFixed(0)}).`);
      return;
    }
    if (!form.exchange_id) { toast.error('Elegi un exchange'); return; }
    setGuardando(true);
    try {
      await axios.patch(`${API}/bots/${instance.bot_id}`, {
        exchange_id: form.exchange_id,
        market_type: form.market_type,
        leverage: parseInt(form.leverage) || 1,
        budget_usd: parseFloat(form.budget_usd) || null,
        strategy_params: {
          usd_por_trade: parseFloat(form.usd_por_trade) || 50,
          stop_loss_pct: form.stop_loss_pct !== '' ? parseFloat(form.stop_loss_pct) : null,
          take_profit_pct: form.take_profit_pct !== '' ? parseFloat(form.take_profit_pct) : null,
        },
      }, { withCredentials: true });
      toast.success('Datos guardados -- arrancala cuando quieras');
      seedRef.current = null;   // que vuelva a leer lo guardado
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setGuardando(false);
  };
  const candidatos = instance?.last_atenea_candidates || [];
  const cierres = (instance ? histories[instance.bot_id] || [] : [])
    .filter(t => t.profit_loss_usd != null)
    .slice().sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
  let _acum = 0;
  const serie = cierres.map(t => { _acum += t.profit_loss_usd; return { fecha: new Date(t.timestamp).toLocaleDateString(), pnl: Number(_acum.toFixed(2)) }; });
  // El símbolo asignado al bot (instance.symbol) queda pegado al último que
  // realmente ganó una entrada -- si el bot está plano evaluando otras
  // candidatas, ese campo sigue mostrando el viejo, dando la impresión de
  // que sigue "operando" ahí cuando en realidad no hay ninguna posición
  // abierta. Se calcula acá mismo, con los mismos datos que ya se traen
  // (sin pedir nada nuevo al servidor), si existe de verdad una posición
  // abierta para este bot -- mismo criterio que usa el propio backend
  // (profit_loss_usd null y no cerrada).
  const hayPosicionAbierta = (instance ? histories[instance.bot_id] || [] : [])
    .some(t => t.profit_loss_usd == null && t.closed !== true);
  const statsPorSimbolo = {};
  cierres.forEach(t => {
    const s = t.symbol;
    if (!statsPorSimbolo[s]) statsPorSimbolo[s] = { trades: 0, pnl: 0, ganadas: 0 };
    statsPorSimbolo[s].trades += 1;
    statsPorSimbolo[s].pnl += t.profit_loss_usd;
    if (t.profit_loss_usd > 0) statsPorSimbolo[s].ganadas += 1;
  });
  // Al cerrar una posición, el cierre se guarda como un trade NUEVO (con
  // el P&L real) y el de apertura queda marcado closed=true pero sin su
  // propio profit_loss_usd -- esa fila de apertura huérfana no aporta
  // nada (ya está resumida en la fila de cierre, como "Apertura → Cierre")
  // y antes se mostraba aparte, duplicando cada operación en dos filas.
  // Mismo filtro que ya usa Bots.js para el resto de la plataforma.
  // BUG encontrado y corregido: el corte a 12 escondía operaciones viejas
  // aunque el cuadro ya tiene scroll propio (max-h con overflow-y-auto) --
  // con más de 12 trades, las más viejas quedaban invisibles sin ningún
  // aviso. Sube a 200 (backend ya trae hasta 5000) para que el scroll
  // muestre el historial real completo, no un recorte artificial.
  const ultimosTrades = (instance ? histories[instance.bot_id] || [] : [])
    .filter(t => !(t.closed && t.profit_loss_usd == null))
    .slice(0, 200);

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <AteneaIcon size={26} />
          <div>
            <h1 className="text-xl font-bold text-white">Atenea IA</h1>
            <p className="text-[#A1A1AA] text-xs">Ella elige la cripto y la dirección — vos configurás exchange, riesgo y protección.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-start">

          {/* ══════════ COLUMNA IZQUIERDA: configuración compacta ══════════ */}
          <div className="space-y-3">
            <Card className="bg-[#121212] border-[#00D4FF]/20">
              <CardContent className="pt-3 pb-3 space-y-1">
                <Label className="text-[10px] text-[#00D4FF] uppercase tracking-wide">Exchange</Label>
                <Select value={editable ? form.exchange_id : instance.exchange_id}
                  disabled={!editable}
                  onValueChange={(v) => setForm({ ...form, exchange_id: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Exchange" /></SelectTrigger>
                  <SelectContent>
                    {exchanges.map((ex) => (
                      <SelectItem key={ex.key_id} value={ex.exchange_id}>{ex.exchange_id.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-[#00FF88]/20">
              <CardContent className="pt-3 pb-3 space-y-2">
                <Label className="text-[10px] text-[#00FF88] uppercase tracking-wide">Mercado y monto</Label>
                <Select value={editable ? form.market_type : instance.market_type}
                  disabled={!editable}
                  onValueChange={(v) => setForm({ ...form, market_type: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spot">Spot</SelectItem>
                    <SelectItem value="futures">Futures</SelectItem>
                  </SelectContent>
                </Select>
                {editable && form.market_type === 'futures' && (
                  <div>
                    <Label className="text-[10px] text-[#52525B]">Apalancamiento: {form.leverage}x</Label>
                    <Input type="range" min="1" max="16" value={form.leverage}
                      onChange={(e) => setForm({ ...form, leverage: e.target.value })} className="w-full h-6" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[9px] text-[#52525B]">USD/op</Label>
                    <Input type="number" min="1" step="1" disabled={!editable}
                      value={editable ? form.usd_por_trade : (instance.strategy_params?.usd_por_trade ?? '—')}
                      onChange={(e) => setForm({ ...form, usd_por_trade: e.target.value })}
                      className="bg-[#111] border-[#27272A] text-white text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[9px] text-[#52525B]">Presupuesto</Label>
                    <Input type="number" min="1" step="1" disabled={!editable}
                      value={editable ? form.budget_usd : (instance.budget_usd ?? '—')}
                      onChange={(e) => setForm({ ...form, budget_usd: e.target.value })}
                      className="bg-[#111] border-[#27272A] text-white text-xs h-8" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#00D4FF]/10 to-[#00FF88]/10 border-[#27272A]">
              <CardContent className="pt-3 pb-3 space-y-2">
                <Label className="text-[10px] text-white uppercase tracking-wide">Stop Loss / Take Profit</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[9px] text-[#52525B]">SL %</Label>
                    <Input type="number" min="0" step="0.5" placeholder="Auto" disabled={!editable}
                      value={editable ? form.stop_loss_pct : (instance.strategy_params?.stop_loss_pct ?? '')}
                      onChange={(e) => setForm({ ...form, stop_loss_pct: e.target.value })}
                      className="bg-[#111] border-[#27272A] text-white text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[9px] text-[#52525B]">TP %</Label>
                    <Input type="number" min="0" step="0.5" placeholder="Auto" disabled={!editable}
                      value={editable ? form.take_profit_pct : (instance.strategy_params?.take_profit_pct ?? '')}
                      onChange={(e) => setForm({ ...form, take_profit_pct: e.target.value })}
                      className="bg-[#111] border-[#27272A] text-white text-xs h-8" />
                  </div>
                </div>
                <p className="text-[9px] text-[#52525B] leading-tight">
                  Vacío = protegida solo por el freno automático por ATR.
                </p>
              </CardContent>
            </Card>

            {!instance ? (
              <Button onClick={activar} className="w-full bg-gradient-to-r from-[#00D4FF] to-[#00FF88] text-black font-bold hover:opacity-90">
                Activar Atenea
              </Button>
            ) : (
              <Card className="bg-[#121212] border-[#27272A]">
                <CardContent className="pt-3 pb-3 space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                      instance.status === 'running' ? 'bg-[#00FF88]/10 text-[#00FF88]' :
                      instance.status === 'error' ? 'bg-red-500/10 text-red-500' : 'bg-[#52525B]/10 text-[#52525B]'
                    }`}>{instance.status}</span>
                    {/* Mismo indicador PAPER/REAL que ya usa el resto de la
                        plataforma (Bots.js) -- lo decide el servidor según
                        si hay inversión real activa, no se elige acá. */}
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${instance.paper_trading ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#FFD600]/10 text-[#FFD600]'}`}>
                      {instance.paper_trading ? 'PAPER' : 'REAL'}
                    </span>
                    <span className="text-white font-mono text-xs ml-auto">
                      {hayPosicionAbierta ? instance.symbol
                        : instance.last_atenea_pendiente
                          ? `revisando ${instance.last_atenea_pendiente.symbol}...`
                          : 'buscando oportunidad...'}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    {instance.status === 'running' ? (
                      <Button onClick={() => stop(instance.bot_id)} className="flex-1 h-8 text-xs bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20">
                        <Square size={12} className="mr-1" /> Detener
                      </Button>
                    ) : (
                      <Button onClick={() => start(instance.bot_id)} className="flex-1 h-8 text-xs bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20 hover:bg-[#00FF88]/20">
                        <Play size={12} className="mr-1" /> Iniciar
                      </Button>
                    )}
                    <Button onClick={() => eliminar(instance.bot_id)} variant="outline" className="h-8 text-xs border-[#27272A]">Quitar</Button>
                  </div>
                  {/* Parada, Atenea se puede reconfigurar: exchange, spot/futuros,
                      apalancamiento, presupuesto y monto por ciclo. Corriendo, los
                      campos quedan bloqueados. */}
                  {editable && (
                    <Button onClick={guardarCambios} disabled={guardando}
                      className="w-full h-8 text-xs bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20 hover:bg-[#00D4FF]/20">
                      {guardando ? 'Guardando...' : 'Guardar cambios'}
                    </Button>
                  )}
                  {editable && (
                    <p className="text-[9px] text-[#52525B] leading-tight">
                      Esta parada: podes cambiarle el exchange, spot/futuros, el
                      apalancamiento, el presupuesto y el monto por ciclo. Guardá y
                      despues arrancala.
                    </p>
                  )}
                  {instance.last_error && (
                    <p className="text-[9px] text-red-400 bg-red-500/5 rounded p-1 flex items-start gap-1">
                      <AlertTriangle size={11} className="shrink-0 mt-0.5" /> {instance.last_error}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* ══════════ ÁREA PRINCIPAL: resumen + gráfico + historial, apilados ══════════ */}
          <div className="space-y-4">

            {instance && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-[#00FF88]/10 to-[#00D4FF]/5 border border-[#00FF88]/20 rounded-lg p-3">
                  <p className="text-[9px] text-[#A1A1AA] uppercase tracking-wide">P&L total</p>
                  <p className={`font-bold mono text-lg ${(instance?.total_profit_usd || 0) >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                    ${formatPnl(instance?.total_profit_usd || 0)}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-[#00D4FF]/10 to-[#00FF88]/5 border border-[#00D4FF]/20 rounded-lg p-3">
                  <p className="text-[9px] text-[#A1A1AA] uppercase tracking-wide">Trades</p>
                  <p className="font-bold mono text-lg text-white">{instance?.total_trades ?? 0}</p>
                </div>
                <div className="bg-gradient-to-r from-[#00D4FF]/10 to-[#00FF88]/10 border border-[#27272A] rounded-lg p-3">
                  <p className="text-[9px] text-[#A1A1AA] uppercase tracking-wide">Última acción</p>
                  <p className={`font-bold text-sm ${instance?.last_action === 'buy' ? 'text-[#00FF88]' : instance?.last_action === 'sell' ? 'text-[#FF3B30]' : 'text-[#00D4FF]'}`}>
                    {instance?.last_action?.toUpperCase() || 'HOLD'}
                    {instance?.last_cycle_at && <span className="text-[#52525B] font-normal ml-1">{new Date(instance.last_cycle_at).toLocaleTimeString()}</span>}
                  </p>
                </div>
              </div>
            )}

            <Card className="bg-[#121212] border-[#27272A]">
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] mb-1"><span className="text-[#00FF88]">P&L</span> <span className="text-[#00D4FF]">acumulado</span></p>
                {serie.length > 0 ? (
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={serie} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <XAxis dataKey="fecha" tick={{ fill: '#A1A1AA', fontSize: 9 }} />
                      {/* Dominio automático con margen -- con pocos cierres y
                          montos chicos ($0.003), un eje fijo 0-4 aplastaba
                          el punto real contra el piso y parecía vacío. */}
                      <YAxis tick={{ fill: '#A1A1AA', fontSize: 9 }} domain={['auto', 'auto']}
                        tickFormatter={(v) => `$${v}`} width={45} />
                      <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, color: '#fff' }}
                        formatter={(v) => [`$${v}`, 'Acumulado']} />
                      <Line type="monotone" dataKey="pnl" stroke="#00D4FF" strokeWidth={2}
                        dot={{ r: 3, fill: '#00D4FF', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-[10px] text-[#52525B] text-center py-6">Sin cierres todavía</p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-[#27272A]">
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-[#00FF88] uppercase tracking-wide mb-2">Historial reciente</p>
                {ultimosTrades.length === 0 ? (
                  <p className="text-[10px] text-[#52525B] text-center py-4">Sin operaciones todavía</p>
                ) : (
                  <div className="space-y-1 max-h-[260px] overflow-y-auto pr-1">
                    {/* Encabezado de columnas -- mismas proporciones que cada
                        fila de abajo, para que todo quede alineado en vez de
                        corrido según el largo de cada dato. */}
                    <div className="grid grid-cols-[46px_1fr_1fr_66px_52px] gap-1.5 px-2 text-[8px] text-[#52525B] uppercase tracking-wide">
                      <span>Cripto</span><span>Apertura</span><span>Cierre</span><span className="text-right">P&L</span><span className="text-right">Hora</span>
                    </div>
                    {ultimosTrades.map((t) => {
                      // Pedido explícito: siempre 8 decimales, igual que el P&L --
                      // antes truncaba a 2 (precios >=1) o 6 (precios <1).
                      const fmtPx = (p) => p == null ? '—' : Number(p).toFixed(8);
                      const esCierre = t.profit_loss_usd != null;
                      // Mientras la posición sigue abierta, el precio actual sale
                      // del escáner en vivo (mismo dato que ya se pide cada ciclo,
                      // sin llamada extra) -- una vez cierra, el precio de cierre
                      // queda fijo en t.price para siempre.
                      const precioActual = !esCierre ? candidatos.find(c => c.symbol === t.symbol)?.precio : null;
                      // t.action es la acción con la que se EJECUTÓ este
                      // registro. En una fila YA CERRADA, ese registro es el
                      // de CIERRE (ej: se abrió con BUY, se cierra con SELL)
                      // -- la apertura fue la acción contraria. BUG
                      // encontrado y corregido: en una fila TODAVÍA ABIERTA
                      // no hay ningún cierre todavía -- t.action YA ES la
                      // acción de apertura real, invertirla mostraba "BUY"
                      // para una posición que en realidad se abrió en corto
                      // (SELL), aunque el P&L sí se calculaba bien con la
                      // acción real -- la etiqueta no coincidía con el número.
                      const accionCierre = t.action;
                      const accionApertura = esCierre ? (accionCierre === 'buy' ? 'sell' : 'buy') : t.action;
                      const colorAccion = (a) => a === 'buy' ? 'text-[#00FF88]' : 'text-[#FF3B30]';
                      // P&L EN VIVO de la posición todavía abierta -- antes
                      // esta columna solo decía "abierta" sin ningún valor;
                      // ahora calcula la ganancia/pérdida flotante con el
                      // mismo precio en vivo que ya trae el escáner.
                      const pnlEnVivo = (!esCierre && precioActual != null && t.quantity)
                        ? (t.action === 'buy' ? (precioActual - t.price) : (t.price - precioActual))
                          * t.quantity * (instance?.leverage || 1)
                        : null;
                      return (
                      <div key={t.trade_id} className="grid grid-cols-[46px_1fr_1fr_66px_52px] items-center gap-1.5 text-[10px] bg-gradient-to-r from-[#00D4FF]/5 to-[#00FF88]/5 border border-[#27272A] rounded px-2 py-1.5">
                        <span className="font-mono text-white font-bold truncate">{(t.symbol || '').replace('/USDT', '')}</span>
                        <span className="font-mono text-[#A1A1AA] truncate">
                          <span className={`font-bold ${colorAccion(accionApertura)}`}>{accionApertura.toUpperCase()}</span> ${fmtPx(t.entry_price ?? t.price)}
                        </span>
                        <span className="font-mono text-[#A1A1AA] truncate">
                          {esCierre ? (
                            <><span className={`font-bold ${colorAccion(accionCierre)}`}>{accionCierre.toUpperCase()}</span> ${fmtPx(t.price)}</>
                          ) : precioActual != null ? (
                            <>Actual: <span className={(pnlEnVivo ?? 0) >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}>${fmtPx(precioActual)}</span></>
                          ) : '—'}
                        </span>
                        {esCierre ? (
                          <span className={`font-mono font-bold text-right ${t.profit_loss_usd >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                            {t.profit_loss_usd >= 0 ? '+' : ''}${formatPnl(t.profit_loss_usd)}
                          </span>
                        ) : pnlEnVivo != null ? (
                          <span className={`font-mono font-bold text-right ${pnlEnVivo >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                            {pnlEnVivo >= 0 ? '+' : ''}${formatPnl(pnlEnVivo)}
                          </span>
                        ) : (
                          <span className="text-[#52525B] text-right">abierta</span>
                        )}
                        <span className="text-[#52525B] text-right">{t.timestamp ? new Date(t.timestamp).toLocaleTimeString() : ''}</span>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ══════════ ESCÁNER -- ahora ancho completo, debajo de todo ══════════ */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-white font-bold text-sm">
                Escáner en vivo <span className="text-[#00D4FF]">·</span> <span className="text-[#00FF88]">{candidatos.length || '—'}</span> criptos
              </p>
              {instance?.last_atenea_scan_at && (
                <span className="text-[10px] text-[#52525B] flex items-center gap-1">
                  <RefreshCw size={10} /> {new Date(instance.last_atenea_scan_at).toLocaleTimeString()}
                </span>
              )}
            </div>
            {!instance ? (
              <p className="text-xs text-[#52525B] text-center py-10">Activá Atenea para que empiece a escanear el mercado en vivo.</p>
            ) : candidatos.length === 0 ? (
              <p className="text-xs text-[#52525B] text-center py-10">
                <RefreshCw size={14} className="inline mr-1 animate-spin" /> Esperando el primer barrido...
              </p>
            ) : (
              <div className="space-y-1 max-h-[620px] overflow-y-auto pr-1">
                {candidatos.map((c, idx) => {
                      const esActual = hayPosicionAbierta && c.symbol === instance.symbol;
                      // Puede haber VARIAS candidatas pendientes a la vez en el
                      // mismo ciclo (ej. 3 esperando su propio cierre de vela) --
                      // se marcan todas, no solo la primera/mejor.
                      const pendientes = instance.last_atenea_pendientes || [];
                      const pendiente = pendientes.find(p => p.symbol === c.symbol);
                      const esPendiente = !!pendiente;
                      // Las 6 que Atenea revisa en profundidad este ciclo --
                      // las haya confirmado, rechazado o dejado pendiente. Se
                      // marca aparte de "REVISANDO" (esa es solo para la que
                      // tiene una señal real esperando confirmar).
                      const enRevision = (instance.last_atenea_en_revision || []).includes(c.symbol);
                      const st = statsPorSimbolo[c.symbol];
                      return (
                        <div key={c.symbol}
                          className={`flex items-center gap-2.5 flex-wrap px-2.5 py-1.5 rounded text-xs transition-all border ${
                            esActual ? 'bg-gradient-to-r from-[#00D4FF]/20 to-[#00FF88]/15 border-[#00D4FF]/50' :
                            idx % 2 === 0 ? 'bg-gradient-to-r from-[#00D4FF]/[0.04] to-transparent border-[#27272A]/60' :
                                            'bg-gradient-to-r from-[#00FF88]/[0.04] to-transparent border-[#27272A]/60'}`}>
                          <ScoreBadge score={c.score} />
                          <div className="min-w-[70px]">
                            <span className="text-white font-bold">{c.symbol.replace('/USDT', '')}</span>
                            <span className="text-[#52525B] text-[9px]">/USDT</span>
                          </div>
                          <span className={`flex items-center gap-1 font-bold min-w-[55px] ${c.direccion === 'ALZA' ? 'text-[#00FF88]' : c.direccion === 'BAJA' ? 'text-[#FF3B30]' : 'text-[#52525B]'}`}>
                            {c.direccion === 'ALZA' ? <TrendingUp size={12} /> : c.direccion === 'BAJA' ? <TrendingDown size={12} /> : null}
                            {c.direccion || '—'}
                          </span>
                          <span className="font-mono text-[#A1A1AA] hidden sm:inline">
                            RSI <span className={c.rsi_1h > 70 ? 'text-[#FF3B30]' : c.rsi_1h < 30 ? 'text-[#00FF88]' : 'text-white'}>{c.rsi_1h ?? '—'}</span>
                          </span>
                          <span className="font-mono text-[#A1A1AA] hidden sm:inline">ADX {c.adx_1h ?? '—'}</span>
                          {c.timeframes_alineados != null && (
                            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded hidden lg:inline ${
                              c.timeframes_alineados === 3 ? 'bg-[#00FF88]/10 text-[#00FF88]' :
                              c.timeframes_alineados === 2 ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#27272A] text-[#A1A1AA]'}`}>
                              {c.timeframes_alineados}/3 TF
                            </span>
                          )}
                          {c.pa_estructura && (
                            <span className={`text-[9px] hidden xl:inline ${c.pa_estructura === 'HH_HL' ? 'text-[#00FF88]' : c.pa_estructura === 'LH_LL' ? 'text-[#FF3B30]' : 'text-[#52525B]'}`}>
                              {c.pa_estructura === 'HH_HL' ? '📈 HH/HL' : c.pa_estructura === 'LH_LL' ? '📉 LH/LL' : 'mixta'}
                            </span>
                          )}
                          {c.etapa_tendencia && (
                            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded hidden lg:inline ${
                              c.etapa_tendencia === 'joven (punto ideal)' ? 'bg-[#00FF88]/10 text-[#00FF88]' :
                              c.etapa_tendencia === 'agotada (riesgo de giro)' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' :
                              c.etapa_tendencia === 'recién arrancando (sin confirmar)' ? 'bg-[#FFA500]/10 text-[#FFA500]' :
                              'bg-[#27272A] text-[#A1A1AA]'}`}
                              title={c.edad_tendencia != null ? `${c.edad_tendencia} velas con ADX≥25` : ''}>
                              {c.etapa_tendencia}
                            </span>
                          )}
                          {c.vela_confirma === false && (
                            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded hidden lg:inline bg-[#FF3B30]/10 text-[#FF3B30]"
                              title={c.vela_veredicto || 'La última vela contradice la dirección sugerida'}>
                              ⚠️ vela en contra
                            </span>
                          )}
                          <span className={`font-mono hidden md:inline ${c.cambio_24h >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                            {c.cambio_24h != null ? `${c.cambio_24h >= 0 ? '+' : ''}${c.cambio_24h}%` : '—'}
                          </span>
                          <span className="text-white font-mono ml-auto">
                            {c.precio != null ? `$${c.precio < 1 ? c.precio.toFixed(6) : c.precio.toFixed(2)}` : '—'}
                          </span>
                          {st && (
                            <span className="flex items-center gap-1 text-[9px] font-mono bg-[#0d0d0d] border border-[#27272A] rounded px-1.5 py-0.5">
                              <span className="text-[#52525B]">{st.trades}t</span>
                              <span className={st.pnl >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}>{st.pnl >= 0 ? '+' : ''}${formatPnl(st.pnl)}</span>
                              <span className="text-[#00D4FF]">{Math.round(st.ganadas / st.trades * 100)}%</span>
                            </span>
                          )}
                          {esActual && (
                            <span className="text-[8px] font-bold text-black bg-gradient-to-r from-[#00D4FF] to-[#00FF88] px-1.5 py-0.5 rounded-full">OPERANDO</span>
                          )}
                          {!esActual && esPendiente && (
                            <span className="text-[8px] font-bold text-black bg-yellow-400 px-1.5 py-0.5 rounded-full"
                              title={pendiente.motivo || ''}>
                              REVISANDO ({pendiente.accion === 'buy' ? 'compra' : 'venta'})
                            </span>
                          )}
                          {!esActual && !esPendiente && enRevision && (
                            <span className="text-[8px] font-bold text-[#00D4FF] bg-[#00D4FF]/10 border border-[#00D4FF]/30 px-1.5 py-0.5 rounded-full"
                              title="Una de las 6 que Atenea analiza a fondo este ciclo">
                              EN ANÁLISIS
                            </span>
                          )}
                        </div>
                      );
                    })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AteneaTrader;
